import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  Bell,
  Building2,
  ChevronDown,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  PackageSearch,
  Settings,
  ShipWheel,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/features/auth/auth-context";
import { useTenantContext } from "@/features/auth/tenant-context";
import { roleLabel } from "@/features/auth/roles";

const navItems = [
  { label: "Dashboard", to: "/app/dashboard", icon: LayoutDashboard, active: true },
  { label: "Shipments", to: "/shipments", icon: PackageSearch, active: true },
  { label: "Customers", to: "/customers", icon: Users, active: true },
  { label: "Documents", to: "/app/documents", icon: FileText, active: false },
  { label: "Settings", to: "/app/settings", icon: Settings, active: false },
];

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { signOut, user } = useAuth();
  const { activeTenant, activeMembership, tenants, setActiveTenant, profileName, isLoading, error } = useTenantContext();

  async function handleSignOut() {
    await signOut();
  }

  const sidebar = (
    <aside className="flex h-full w-72 flex-col bg-slate-950 text-slate-300">
      <div className="flex h-20 items-center gap-3 border-b border-white/10 px-6">
        <span className="grid size-9 place-items-center rounded-xl bg-sky-400 text-slate-950">
          <ShipWheel className="size-5" />
        </span>
        <div>
          <p className="text-sm font-bold tracking-wide text-white">SHIPMENT</p>
          <p className="text-[10px] font-semibold tracking-[0.28em] text-sky-300">INTELLIGENCE</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-4 py-6">
        <p className="px-3 pb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Workspace</p>
        {navItems.map(({ label, to, icon: Icon, active }) => (
          <NavLink
            key={label}
            to={to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors ${
                isActive && active ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="size-4" />
            <span>{label}</span>
            {!active && <Badge className="ml-auto bg-white/10 px-2 py-0.5 text-[10px] text-slate-400">Soon</Badge>}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 p-4">
        <div className="rounded-xl bg-white/5 p-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-300">
            <Bell className="size-3.5 text-sky-300" />
            Workspace status
          </div>
          <p className="text-xs leading-5 text-slate-500">Foundation setup is active. Operational modules arrive in later phases.</p>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="fixed inset-y-0 left-0 z-40 hidden lg:block">{sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button className="absolute inset-0 bg-slate-950/60" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />
          <div className="relative h-full">{sidebar}</div>
        </div>
      )}

      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 flex h-20 items-center justify-between border-b border-slate-200 bg-white/95 px-4 backdrop-blur sm:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="px-2 lg:hidden" aria-label="Open navigation" onClick={() => setMobileOpen(true)}>
              <Menu className="size-5" />
            </Button>
            <div className="hidden text-sm text-slate-500 sm:block">Operations workspace</div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative group">
              <label className="sr-only" htmlFor="tenant-switcher">Current company</label>
              <select
                id="tenant-switcher"
                value={activeTenant?.id ?? ""}
                disabled={isLoading || tenants.length < 2}
                onChange={(event) => setActiveTenant(event.target.value)}
                className="h-10 max-w-48 appearance-none rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-8 text-sm font-medium text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:cursor-default disabled:bg-slate-50"
              >
                {tenants.length === 0 && <option value="">No company assigned</option>}
                {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
              </select>
              <Building2 className="pointer-events-none absolute left-3 top-3 size-4 text-slate-400" />
              <ChevronDown className="pointer-events-none absolute right-2.5 top-3 size-4 text-slate-400" />
            </div>
            <div className="hidden items-center gap-3 border-l border-slate-200 pl-4 sm:flex">
              <div className="grid size-9 place-items-center rounded-full bg-slate-900 text-xs font-bold text-white">
                {(profileName ?? user?.email ?? "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="hidden min-w-0 md:block">
                <p className="max-w-44 truncate text-sm font-semibold text-slate-800">{profileName || user?.email}</p>
                <p className="text-xs text-slate-500">{roleLabel(activeMembership?.role ?? null)}</p>
              </div>
              <Button variant="ghost" className="px-2" title="Sign out" aria-label="Sign out" onClick={handleSignOut}>
                <LogOut className="size-4" />
              </Button>
            </div>
            <Button variant="ghost" className="px-2 sm:hidden" aria-label="Close account menu" onClick={handleSignOut}>
              <LogOut className="size-4" />
            </Button>
          </div>
        </header>

        <main className="mx-auto max-w-[1600px] px-4 py-8 sm:px-8">
          {error && <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
          {tenants.length === 0 && !isLoading && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Your account is authenticated, but it has no tenant membership yet. Ask an administrator to add your account.
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
